/* eslint-disable import/prefer-default-export */
import get from "lodash/get";
import includes from "lodash/includes";
import {
  COMPETITION_SCORE_TYPE,
  TEAM_QUALIFICATION_SCORE_DETERMINATION,
} from "../../../../../constants/competitionConstants";
import isBlank from "../../../../../utils/isBlank";

/**
 * Generate the validation schema for the team field dynamically based on the `teamSelectionRequired` value.
 */
export function schemaRegistrationCompetitionTeamSelectionRequired(
  fieldSchema,
  isCompetitionSelectedFieldName = "selected",
  teamSelectionRequiredFieldName = "teamSelectionRequired",
) {
  return fieldSchema.when(
    [isCompetitionSelectedFieldName, teamSelectionRequiredFieldName],
    (isCompetitionSelected, teamSelectionRequired, _schema) => {
      if (!isCompetitionSelected || !teamSelectionRequired) {
        return _schema;
      }

      return _schema.test("teamSelectionIsRequired", "A team must be selected.", function checkTeamSelectionIsRequired(
        teamValue,
      ) {
        return !isBlank(teamValue);
      });
    },
  );
}

/**
 * Generate the validation schema for the team position field dynamically based on the `teamPositionRequired` value.
 */
export function schemaRegistrationCompetitionTeamPositionSelectionRequired(
  fieldSchema,
  isCompetitionSelectedFieldName = "selected",
  teamPositionSelectionRequiredFieldName = "teamPositionRequired",
  teamFieldName = "team",
) {
  return fieldSchema.when(
    [isCompetitionSelectedFieldName, teamPositionSelectionRequiredFieldName, teamFieldName],
    (isCompetitionSelected, teamPositionSelectionRequired, team, _schema) => {
      if (!isCompetitionSelected || !teamPositionSelectionRequired || isBlank(team)) {
        return _schema;
      }

      return _schema.test(
        "teamPositionSelectionIsRequired",
        "A team position must be selected.",
        function checkTeamPositionSelectionIsRequired(teamPositionValue) {
          return !isBlank(teamPositionValue);
        },
      );
    },
  );
}

export function shouldDisplayIndividualQualificationScore(competition) {
  const competitionQualificationScoreType = get(competition, "qualificationScoreType", null);

  if (!includes(Object.values(COMPETITION_SCORE_TYPE), competitionQualificationScoreType)) {
    return false;
  }

  if (
    get(competition, "teamCompetition", false) &&
    get(competition, "teamQualificationScoreDetermination", "") ===
      TEAM_QUALIFICATION_SCORE_DETERMINATION.PROVIDED_EXPLICITLY
  ) {
    return false;
  }

  return true;
}

/**
 * Determines the participant's own age group from the competition's age groups.
 * Filters to age groups where participantAge falls within [lower_limit, upper_limit] (inclusive).
 * For multiple matching brackets (e.g. masters sports overlapping brackets like 25-120, 30-120, 35-120),
 * selects the most specific bracket (tightest span: upper_limit - lower_limit).
 * Ties are broken by highest lower_limit.
 */
export function getParticipantOwnAgeGroup(ageGroups, participantAge) {
  if (participantAge == null || !Array.isArray(ageGroups) || ageGroups.length === 0) {
    return null;
  }

  const matching = ageGroups.filter(ag => {
    if (!ag || ag.lower_limit == null) {
      return false;
    }
    if (participantAge < ag.lower_limit) {
      return false;
    }
    if (ag.upper_limit != null && participantAge > ag.upper_limit) {
      return false;
    }
    return true;
  });

  if (matching.length === 0) {
    return null;
  }

  return matching.reduce((best, current) => {
    if (!best) {
      return current;
    }
    const bestSpan = (best.upper_limit != null ? best.upper_limit : 999999) - best.lower_limit;
    const currentSpan = (current.upper_limit != null ? current.upper_limit : 999999) - current.lower_limit;

    if (currentSpan < bestSpan) {
      return current;
    }
    if (currentSpan === bestSpan && current.lower_limit > best.lower_limit) {
      return current;
    }
    return best;
  }, null);
}

/**
 * Determines the age group ID that should be auto-populated when a team is selected.
 *
 * Rules:
 * 1. If the team has a saved age group in backend (calculatedAgeGroupId):
 *    - If the participant is younger than the team's age division (participantAge < teamAgeGroup.lower_limit):
 *      The age group drops to the younger participant's own age division.
 *    - Otherwise (participant is in or older than the team's division):
 *      Populates with the team's saved age group.
 * 2. If the team does not have a saved age group (e.g. newly created team with no members yet):
 *    - Populates with the participant's own age division.
 */
export function resolveTeamAgeGroupId({ team, ageGroups, participantAge }) {
  if (!team || !Array.isArray(ageGroups) || ageGroups.length === 0) {
    return null;
  }

  const teamSavedAgeGroupId = team.calculatedAgeGroupId || team.calculated_age_group_id || null;
  const teamSavedAgeGroup = teamSavedAgeGroupId ? ageGroups.find(ag => ag && ag.id === teamSavedAgeGroupId) : null;

  if (teamSavedAgeGroup) {
    const isParticipantYounger =
      participantAge != null && teamSavedAgeGroup.lower_limit != null && participantAge < teamSavedAgeGroup.lower_limit;

    if (isParticipantYounger) {
      const ownAgeGroup = getParticipantOwnAgeGroup(ageGroups, participantAge);
      return ownAgeGroup ? ownAgeGroup.id : teamSavedAgeGroup.id;
    }

    return teamSavedAgeGroup.id;
  }

  const ownAgeGroup = getParticipantOwnAgeGroup(ageGroups, participantAge);
  return ownAgeGroup ? ownAgeGroup.id : teamSavedAgeGroupId || null;
}
